import { ErrorLogComponent } from '@cian/error-log-component';
import { type IVisitedOffersProviderProps, VisitedOffersProvider } from '@cian/frontend-visited-offers-widget';
import { Provider as MicrofrontendProvider } from '@cian/mf-react';
import { UiKitRoot } from '@cian/ui-kit/root/UiKitRoot';

import { type FC, StrictMode } from 'react';

import { PageContainer } from '@/app/shared/containers/PageContainer';
import { AliasesContext } from '@/app/shared/context/AliasesContext';
import { ApplicationContextProvider } from '@/app/shared/context/ApplicationContext';
import { ReduxStateContextProvider } from '@/app/shared/context/ReduxStoreContext';

import { NotificationCenterProvider } from '@/packages/notifications-center';
import { QueryStateProvider } from '@/packages/query-state/shared/components/QueryStateProvider';

import { ActionsCentersLoadable } from './internal/containers/ActionsCenters/ActionsCentersLoadable';
import { type ApplicationProps } from './types';

export const Application: FC<ApplicationProps> = props => {
  const { context, queryState, reduxState, mfAliases } = props;

  const {
    logger,
    custom: { initialDeviceType },
    microfrontendsRegistry,
    config,
  } = context;

  const { settings, enabled } = config.get<IVisitedOffersProviderProps>('audience.visitedOffers') || {};

  return (
    <StrictMode>
      <ErrorLogComponent logger={logger}>
        <NotificationCenterProvider />
        <ApplicationContextProvider value={context}>
          <MicrofrontendProvider registry={microfrontendsRegistry}>
            <AliasesContext.Provider value={mfAliases}>
              <ReduxStateContextProvider reduxState={reduxState}>
                <QueryStateProvider appContext={context} devTools={context.custom.isTestMode} queryState={queryState}>
                  <UiKitRoot useStaticDeviceType deviceType={initialDeviceType}>
                    <VisitedOffersProvider enabled={enabled} settings={settings}>
                      <PageContainer />
                      <ActionsCentersLoadable />
                    </VisitedOffersProvider>
                  </UiKitRoot>
                </QueryStateProvider>
              </ReduxStateContextProvider>
            </AliasesContext.Provider>
          </MicrofrontendProvider>
        </ApplicationContextProvider>
      </ErrorLogComponent>
    </StrictMode>
  );
};
